import React, { useEffect, useMemo, useState } from 'react';

// INTERNAL Recommendation Engine — sits ON TOP of the knowledge stack and BELOW
// the audit engine. It creates no facts; it USES high-signal Knowledge Objects +
// strong / explicitly-Conflicted Consensus Groups to produce EXPLAINABLE,
// traceable recommendations. Rendered as a subtab inside the Learning Engine.
// NOT customer-facing. Recommendations are not production rules and never change
// audit results — every one is fully traceable back to the documents behind it.
const API = 'https://tm-audit-platform-production.up.railway.app';

const fmtNum = (n) => (n || 0).toLocaleString();
const fmtPct = (n) => (n == null ? '—' : Math.round(n * 100) + '%');

const READINESS_COLOR = {
    'Ready': 'var(--green)',
    'Needs More Evidence': 'var(--amber)',
    'Experimental': 'var(--blue)',
    'Conflicted': 'var(--red, #c0392b)',
    'Historical': 'var(--text-muted)',
    'Deprecated': 'var(--text-muted)',
};

function Badge({ text, color }) {
    return <span className="le-badge" style={{ color, borderColor: color }}>{text}</span>;
}

function KpiCard({ label, value, accent }) {
    return (
        <div className="gd-kpi">
            <div className="gd-kpi-label">{label}</div>
            <div className="gd-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
        </div>
    );
}

function RecCard({ r, onOpen }) {
    return (
        <div className="le-kb-card le-clickable" onClick={onOpen}>
            <div className="le-kb-head">
                <Badge text={r.readiness} color={READINESS_COLOR[r.readiness] || 'var(--text-muted)'} />
                <Badge text={r.recommendation_type.replace(' Recommendation', '')} color="var(--blue)" />
                {r.created_from === 'consensus-group'
                    ? <span className="le-vtag">consensus</span>
                    : <span className="le-vtag">fact</span>}
            </div>
            <div className="le-kb-title">{r.title}</div>
            <div className="le-kb-body">{r.body}</div>
            <div className="kc-score-row">
                <span className="kc-score" style={{ color: READINESS_COLOR[r.readiness] }}>{fmtPct(r.confidence_score)}</span>
                <span className="kc-score-label">confidence</span>
            </div>
            <div className="le-kb-foot">
                {fmtNum(r.supporting_object_count)} object(s) · {fmtNum(r.supporting_document_count)} doc(s)
                {r.servpro_bulletin_count ? ` · ${r.servpro_bulletin_count} bulletin(s)` : ''}
                {r.conflicting_object_count ? ` · ${r.conflicting_object_count} conflict` : ''}
                {(Array.isArray(r.business_impact) && r.business_impact.length) ? ` · ${r.business_impact.join(', ')}` : ''}
            </div>
        </div>
    );
}

export default function RecommendationEngine({ session }) {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [readinessFilter, setReadinessFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [selectedKey, setSelectedKey] = useState(null);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const getJSON = async (path) => {
        const res = await fetch(API + path, { headers });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        return res.json();
    };

    const load = async () => {
        setLoading(true); setErr(null);
        try { setDashboard(await getJSON('/api/admin/recommendations/dashboard')); }
        catch (e) { setErr(e.message); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const generate = async () => {
        setBusy(true); setMsg(null);
        try {
            const res = await fetch(API + '/api/admin/recommendations/generate', { method: 'POST', headers, body: JSON.stringify({}) });
            const out = await res.json();
            if (!res.ok || !out.success) throw new Error(out.error || `HTTP ${res.status}`);
            setMsg(`✓ ${out.generated} recommendation(s) — ${out.consensus} from consensus · ${out.knowledge} from facts · ${out.deprecated} deprecated.`);
            await load();
        } catch (e) { setMsg('⚠ ' + e.message); }
        setBusy(false);
    };

    const recommendations = useMemo(() => {
        let list = dashboard?.recommendations || [];
        if (readinessFilter !== 'all') list = list.filter(r => r.readiness === readinessFilter);
        if (typeFilter !== 'all') list = list.filter(r => r.recommendation_type === typeFilter);
        return list;
    }, [dashboard, readinessFilter, typeFilter]);

    return (
        <div>
            <div className="le-note">
                Internal Recommendation Engine — sits on top of the knowledge stack and below the audit engine. It creates <b>no facts</b>; it uses high-signal <b>Knowledge Objects</b> and strong / explicitly-conflicted <b>Consensus Groups</b> to produce <b>explainable, traceable</b> recommendations. Every recommendation carries at least one supporting Knowledge Object, source-document traceability, a confidence score, a business impact, a readiness, and an explanation block. <b>Internal intelligence only</b> — recommendations are not production rules, are not customer-facing, and never change audit results.
            </div>

            <div className="kc-pipeline le-pipeline">
                {(dashboard?.pipeline || ['Knowledge Corpus', 'Knowledge Extraction', 'Knowledge Objects', 'Knowledge Quality', 'Knowledge Consensus', 'Recommendation Engine', 'Audit Engine']).map((s, i, a) => (
                    <React.Fragment key={s}>
                        <span className={'le-pipe-step' + (s === 'Recommendation Engine' ? ' kc-pipe-active' : '')}>{s}</span>
                        {i < a.length - 1 && <span className="le-pipe-arrow">→</span>}
                    </React.Fragment>
                ))}
            </div>

            <div className="le-upload">
                <button className={'le-btn le-approve' + (busy ? ' le-disabled' : '')} disabled={busy} onClick={generate}>
                    {busy ? 'Generating…' : '✦ Generate Recommendations'}
                </button>
                <button className="history-back-link" onClick={load}>↻ Refresh</button>
                {msg && <span className={msg.startsWith('⚠') ? 'le-error' : 'le-upload-ok'} style={{ margin: 0 }}>{msg}</span>}
            </div>

            {err && <div className="le-error">⚠ {err}</div>}
            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {!loading && dashboard && (
                <div>
                    <div className="gd-section-title">Recommendation Overview</div>
                    <div className="gd-kpi-grid">
                        <KpiCard label="Recommendations" value={fmtNum(dashboard.cards.total)} />
                        <KpiCard label="Ready" value={fmtNum(dashboard.cards.ready)} accent="var(--green)" />
                        <KpiCard label="Experimental" value={fmtNum(dashboard.cards.experimental)} accent="var(--blue)" />
                        <KpiCard label="Conflicted" value={fmtNum(dashboard.cards.conflicted)} accent="var(--red, #c0392b)" />
                        <KpiCard label="From Consensus" value={fmtNum(dashboard.cards.fromConsensus)} />
                        <KpiCard label="From Facts" value={fmtNum(dashboard.cards.fromKnowledge)} />
                    </div>

                    {/* Part 8 — by business impact */}
                    {(dashboard.byBusinessImpact || []).length > 0 && (
                        <>
                            <div className="gd-section-title">By Business Impact</div>
                            <div className="le-controls">
                                {dashboard.byBusinessImpact.map(b => (
                                    <span key={b.impact} className="le-pill">{b.impact} · {b.count}</span>
                                ))}
                            </div>
                        </>
                    )}

                    {/* readiness + type filters */}
                    <div className="le-controls">
                        <button className={'le-pill ' + (readinessFilter === 'all' ? 'active' : '')} onClick={() => setReadinessFilter('all')}>All readiness</button>
                        {(dashboard.readinessCatalog || []).map(r => (
                            <button key={r} className={'le-pill ' + (readinessFilter === r ? 'active' : '')} onClick={() => setReadinessFilter(r)}>{r}</button>
                        ))}
                    </div>
                    <div className="le-controls">
                        <button className={'le-pill ' + (typeFilter === 'all' ? 'active' : '')} onClick={() => setTypeFilter('all')}>All types</button>
                        {(dashboard.byType || []).filter(t => t.count > 0).map(t => (
                            <button key={t.type} className={'le-pill ' + (typeFilter === t.type ? 'active' : '')} onClick={() => setTypeFilter(t.type)}>{t.type.replace(' Recommendation', '')} · {t.count}</button>
                        ))}
                        <span className="le-count">{recommendations.length} recommendation(s)</span>
                    </div>

                    {dashboard.cards.total === 0 && <div className="le-empty">No recommendations yet. Click “Generate Recommendations” to derive them from the Knowledge Objects + Consensus Groups.</div>}

                    <div className="kc-grid">
                        {recommendations.map(r => (
                            <RecCard key={r.rec_key} r={r} onOpen={() => setSelectedKey(r.rec_key)} />
                        ))}
                    </div>
                </div>
            )}

            {selectedKey && (
                <RecommendationDetail recKey={selectedKey} headers={headers} onClose={() => setSelectedKey(null)} />
            )}
        </div>
    );
}

// ── Recommendation detail drawer (Parts 5 / 8) ──────────────────────────────
function RecommendationDetail({ recKey, headers, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(API + '/api/admin/recommendations/' + encodeURIComponent(recKey), { headers });
                const out = await res.json();
                if (live) setData(out);
            } catch (e) { if (live) setData({ error: e.message }); }
            if (live) setLoading(false);
        })();
        return () => { live = false; };
    }, [recKey, headers]);

    const r = data?.recommendation;
    const ex = data?.explanation;
    return (
        <div className="le-drawer-overlay" onClick={onClose}>
            <div className="le-drawer" onClick={e => e.stopPropagation()}>
                <div className="le-drawer-head"><button className="history-back-link" onClick={onClose}>✕ Close</button></div>
                {loading && <div className="history-loading"><div className="spinner" /></div>}
                {data?.error && <div className="le-error">⚠ {data.error}</div>}
                {!loading && r && (
                    <div className="le-drawer-body">
                        <div className="le-drawer-title">
                            {r.title} <Badge text={r.readiness} color={READINESS_COLOR[r.readiness]} />
                        </div>
                        <div className="le-kb-head" style={{ marginBottom: 8 }}>
                            <Badge text={r.recommendation_type} color="var(--blue)" />
                            {(Array.isArray(r.business_impact) ? r.business_impact : []).map(b => <Badge key={b} text={b} color="var(--text-muted)" />)}
                        </div>

                        <div className="le-note" style={{ marginTop: 0 }}>{r.body}</div>

                        <div className="kc-headline">
                            <span className="kc-score-big" style={{ color: READINESS_COLOR[r.readiness] }}>{fmtPct(r.confidence_score)}</span>
                            <span className="kc-score-sub">confidence · {r.created_from === 'consensus-group' ? 'from a Consensus Group' : 'from a Knowledge Object'}</span>
                        </div>

                        <div className="le-meta-grid" style={{ marginTop: 12 }}>
                            <div><span>Supporting Objects</span><b>{fmtNum(r.supporting_object_count)}</b></div>
                            <div><span>Supporting Docs</span><b>{fmtNum(r.supporting_document_count)}</b></div>
                            <div><span>SERVPRO Bulletins</span><b>{fmtNum(r.servpro_bulletin_count)}</b></div>
                            <div><span>Carrier Documents</span><b>{fmtNum(r.carrier_document_count)}</b></div>
                            <div><span>Consensus Groups</span><b>{fmtNum(r.supporting_consensus_group_count)}</b></div>
                            <div><span>Conflicting Objects</span><b>{fmtNum(r.conflicting_object_count)}</b></div>
                        </div>

                        {r.expected_impact && (
                            <>
                                <div className="gd-section-title">Expected Impact</div>
                                <div className="le-note" style={{ marginTop: 0 }}>{r.expected_impact}</div>
                            </>
                        )}
                        {r.source_context && (
                            <>
                                <div className="gd-section-title">Source Context</div>
                                <div className="le-note" style={{ marginTop: 0 }}>{r.source_context}</div>
                            </>
                        )}

                        {/* Part 5 — explanation block (never a recommendation without it) */}
                        {ex && (
                            <div className="kc-explain">
                                <div className="gd-section-title">Explanation</div>
                                <pre className="kc-explain-body" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{ex.rendered}</pre>
                            </div>
                        )}

                        {/* Part 5 — supporting Knowledge Objects (source of truth) */}
                        {(data.supportingObjects || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Supporting Knowledge Objects ({data.supportingObjects.length})</div>
                                <div className="le-kb-grid">
                                    {data.supportingObjects.map(o => (
                                        <div key={o.id} className="le-kb-card">
                                            <div className="le-kb-head">
                                                <Badge text={o.object_type} color="var(--blue)" />
                                                {o.quality_tier && <Badge text={`${o.quality_tier} · ${Math.round((o.quality_score || 0) * 100)}`} color="var(--green)" />}
                                            </div>
                                            <div className="le-kb-title">{o.title}</div>
                                            <div className="le-kb-body">{o.description}</div>
                                            {o.supporting_evidence?.citation && <div className="kc-cite">“{o.supporting_evidence.citation}”</div>}
                                            <div className="le-kb-foot">
                                                {[o.issuer, o.account, o.carrier, o.bulletin_code].filter(Boolean).join(' · ') || o.document_family || '—'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 5 — supporting Consensus Groups */}
                        {(data.supportingConsensusGroups || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Supporting Consensus Groups ({data.supportingConsensusGroups.length})</div>
                                <div className="le-kb-grid">
                                    {data.supportingConsensusGroups.map(g => (
                                        <div key={g.id} className="le-kb-card">
                                            <div className="le-kb-head">
                                                <Badge text={g.readiness} color={READINESS_COLOR[g.readiness] || 'var(--text-muted)'} />
                                                {g.category && <Badge text={g.category} color="var(--blue)" />}
                                            </div>
                                            <div className="le-kb-title">{g.label}</div>
                                            <div className="le-kb-foot">
                                                {fmtPct(g.agreement_score)} consensus on {g.consensus_value_label || '—'} · {fmtNum(g.evidence_count)} fact(s)
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 5 — supporting source documents (traceability) */}
                        {(data.supportingDocuments || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Source Documents ({data.supportingDocuments.length})</div>
                                <div className="le-kb-grid">
                                    {data.supportingDocuments.map(d => (
                                        <div key={d.id} className="le-kb-card">
                                            <div className="le-kb-title">{d.original_filename || d.document_type || 'Document'}</div>
                                            <div className="le-kb-foot">
                                                {[d.issuer, d.account_client, d.carrier, d.bulletin_code].filter(Boolean).join(' · ') || '—'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 5 — conflicting evidence (clearly marked for Conflicted recs) */}
                        {(data.conflictingEvidence?.contradictions || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Conflicting Evidence ({data.conflictingEvidence.contradictions.length})</div>
                                <div className="kc-contradictions">
                                    {data.conflictingEvidence.contradictions.map((c, i) => (
                                        <div key={i} className="kc-contradiction">
                                            <Badge text={c.type} color={c.type === 'Conflict' ? 'var(--red, #c0392b)' : 'var(--amber)'} />
                                            {c.type === 'Conflict'
                                                ? <span> <b>{c.a?.label}</b> vs <b>{c.b?.label}</b></span>
                                                : <span> <b>{c.from?.label}</b> → <b>{c.to?.label}</b></span>}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 7 — future supplement-outcome feedback loop (placeholder) */}
                        <div className="gd-section-title">Future Outcome Tracking</div>
                        <div className="le-note" style={{ marginTop: 0 }}>
                            This recommendation is future-ready for the supplement-outcome feedback loop. These fields are <b>not captured yet</b> — they will let a contractor's supplement outcome be attributed back to this recommendation.
                        </div>
                        <div className="le-meta-grid">
                            {(data.outcomeFields || []).map(f => (
                                <div key={f.key}><span>{f.label}</span><b style={{ color: 'var(--text-muted)' }}>—</b></div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
